import React, { useState, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import api from '../utils/api';
import { LoadingSpinnerRing } from './common/AppLoadingState';

const AudioPlayer = ({ src, onError, callId }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize WaveSurfer
  useEffect(() => {
    let isDestroyed = false;
    let wavesurfer = null;

    const initWaveSurfer = async () => {
      if (!waveformRef.current || isDestroyed) return;

      try {
        setIsLoading(true);
        setIsPlaying(false);

        // Cleanup previous instance if it exists
        if (wavesurferRef.current) {
          wavesurferRef.current.unAll();
          wavesurferRef.current.destroy();
          wavesurferRef.current = null;
        }
        
        wavesurfer = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: '#3b82f6',
          progressColor: '#2563eb',
          cursorColor: 'transparent',
          barWidth: 2,
          barGap: 1,
          height: 48,
          barRadius: 3,
          normalize: true,
          responsive: false,
          fillParent: true,
          backend: 'WebAudio',
          hideScrollbar: true,
          interact: true,
          minPxPerSec: 30,
          pixelRatio: 1,
          barMinHeight: 1,
          barHeight: 2,
          mediaType: 'audio',
          plugins: []
        });

        if (!isDestroyed) {
          wavesurferRef.current = wavesurfer;

          wavesurfer.on('ready', () => {
            if (!isDestroyed) {
              setDuration(wavesurfer.getDuration());
              setIsLoading(false);
            }
          });

          wavesurfer.on('audioprocess', () => {
            if (!isDestroyed) {
              setCurrentTime(wavesurfer.getCurrentTime());
            }
          });

          wavesurfer.on('finish', () => {
            if (!isDestroyed) {
              setIsPlaying(false);
            }
          });

          wavesurfer.on('error', (err) => {
            console.error('WaveSurfer error:', err);
            onError?.(err);
          });

          await wavesurfer.load(src);
        }
      } catch (error) {
        console.error('Error initializing WaveSurfer:', error);
        onError?.(error);
        if (!isDestroyed) {
          setIsLoading(false);
        }
      }
    };

    initWaveSurfer();

    return () => {
      isDestroyed = true;
      if (wavesurfer) {
        wavesurfer.unAll();
        wavesurfer.destroy();
      }
    };
  }, [src, onError]);

  const togglePlay = () => {
    if (!wavesurferRef.current) return;
    
    if (isPlaying) {
      wavesurferRef.current.pause();
    } else {
      wavesurferRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    try {
      const blob = await api.downloadCallRecording(callId);
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'call-recording.mp3';
      
      // Add to document, click it, and remove it
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      onError?.(error);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-lg p-4">
      {/* Top row with time and download */}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-500">
          <span>{formatTime(currentTime)}</span>
          <span className="mx-2">/</span>
          <span>{formatTime(duration)}</span>
        </div>
        <button
          onClick={handleDownload}
          className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Download
        </button>
      </div>

      {/* Player controls and waveform */}
      <div className="flex items-center space-x-4">
        {/* Play button */}
        <div className="flex-shrink-0">
          <button
            onClick={togglePlay}
            disabled={isLoading}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200 hover:scale-105"
          >
            {isLoading ? (
              <LoadingSpinnerRing tone="onDark" className="!h-5 !w-5" />
            ) : isPlaying ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
              </svg>
            )}
          </button>
        </div>

        {/* Waveform with fixed dimensions */}
        <div className="flex-1 relative">
          <div className="absolute inset-0 overflow-hidden">
            <div 
              ref={waveformRef}
              className={`w-full h-[48px] ${isLoading ? 'opacity-50' : ''}`}
              style={{
                minWidth: '100%',
                position: 'absolute',
                left: 0,
                right: 0
              }}
            />
          </div>
          <div className="w-full h-[48px]" /> {/* Spacer for fixed height */}
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer; 